import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Group, Heading, Text } from '@optiaxiom/react';
import { IconCheckDouble } from '@optimizely/axiom-icons';
import type { RowSelectionState } from '@tanstack/react-table';

import { useGetAgentsWithPagination } from '@frontend/features/agents/hooks/useGetAgentsWithPagination';
import { useInstanceId, useNamespacedNavigate } from '@frontend/hooks';

import {
  useNotificationActions,
  useNotificationsUrlParams,
  useOffsetNotificationList,
  useUnreadCount,
} from '../hooks';
import type { Notification } from '../types';
import { getDeepLinkPath } from '../utils';

import { NotificationTable } from './table-view/NotificationTable';
import { NotificationBulkActionsBar } from './NotificationBulkActionsBar';
import { NotificationFilterBar } from './NotificationFilterBar';

export function NotificationCenterPage() {
  const { instanceId } = useInstanceId();
  const navigate = useNamespacedNavigate();
  const { params, updateParams } = useNotificationsUrlParams();
  const { page, pageSize, status, agentId, view } = params;

  // Data fetching
  const { data, isPending } = useOffsetNotificationList({ page, pageSize, status, agentId });
  const { data: unreadCount } = useUnreadCount();
  const actions = useNotificationActions();

  // Agents for filter dropdown
  const { data: agentsResponse } = useGetAgentsWithPagination({
    instanceId: instanceId || '',
    pageSize: 200,
  });
  const agents = useMemo(
    () => (agentsResponse?.items || []).map(a => ({ id: a.id, name: a.name })),
    [agentsResponse]
  );

  const notifications = data?.items ?? [];
  const pagination = data?.pagination;

  // Row selection (table view)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Clear selection on page/view change
  useEffect(() => {
    setRowSelection({});
  }, [page, view]);

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter(k => rowSelection[k]),
    [rowSelection]
  );

  // Action handlers
  const handleMarkRead = useCallback(
    (id: string) => actions.markAsRead.mutate(id),
    [actions.markAsRead]
  );
  const handleMarkUnread = useCallback(
    (id: string) => actions.markAsUnread.mutate(id),
    [actions.markAsUnread]
  );
  const handleDelete = useCallback(
    (id: string) => actions.deleteNotification.mutate(id),
    [actions.deleteNotification]
  );

  const handleBulkRead = useCallback(() => {
    actions.bulkMarkAsRead.mutate(selectedIds);
    setRowSelection({});
  }, [actions.bulkMarkAsRead, selectedIds]);

  const handleBulkUnread = useCallback(() => {
    actions.bulkMarkAsUnread.mutate(selectedIds);
    setRowSelection({});
  }, [actions.bulkMarkAsUnread, selectedIds]);

  const handleBulkDismiss = useCallback(() => {
    actions.bulkDelete.mutate(selectedIds);
    setRowSelection({});
  }, [actions.bulkDelete, selectedIds]);

  const handleActivate = useCallback(
    (notification: Notification) => {
      if (!notification.is_read) {
        actions.markAsRead.mutate(notification.id);
      }
      navigate(getDeepLinkPath(notification));
    },
    [actions.markAsRead, navigate]
  );

  const handleMarkAllRead = useCallback(() => {
    actions.bulkMarkAsRead.mutate(undefined);
  }, [actions.bulkMarkAsRead]);

  return (
    <Group flexDirection="column" gap="16" px="32" py="24">
      {/* Header */}
      <Group justifyContent="space-between" alignItems="center">
        <Group gap="12" alignItems="center">
          <Heading color="fg.default" level="3">
            Notification Center
          </Heading>
          {unreadCount != null && unreadCount > 0 && (
            <Badge intent="information">{unreadCount} unread</Badge>
          )}
        </Group>
        <Button
          size="sm"
          appearance="subtle"
          icon={<IconCheckDouble />}
          onClick={handleMarkAllRead}
        >
          Mark all as read
        </Button>
      </Group>

      {/* Filters + View Toggle */}
      <NotificationFilterBar
        status={status}
        agentId={agentId}
        view={view}
        agents={agents}
        onStatusChange={s => updateParams({ status: s })}
        onAgentChange={a => updateParams({ agentId: a })}
        onViewChange={v => updateParams({ view: v })}
      />

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <NotificationBulkActionsBar
          selectedCount={selectedIds.length}
          onMarkRead={handleBulkRead}
          onMarkUnread={handleBulkUnread}
          onDismiss={handleBulkDismiss}
        />
      )}

      {/* Views */}
      {view === 'table' && (
        <NotificationTable
          notifications={notifications}
          pagination={pagination}
          isLoading={isPending}
          page={page}
          pageSize={pageSize}
          onPageChange={p => updateParams({ page: p })}
          onPageSizeChange={ps => updateParams({ pageSize: ps })}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          onActivate={handleActivate}
          onMarkRead={handleMarkRead}
          onMarkUnread={handleMarkUnread}
          onDelete={handleDelete}
        />
      )}

      {/* Empty state */}
      {!isPending && notifications.length === 0 && view === 'table' && (
        <Group justifyContent="center" py="48">
          <Text color="fg.tertiary">No notifications</Text>
        </Group>
      )}
    </Group>
  );
}
